import React, { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { TAROT_SPREADS } from 'tarot/spreads';
import {
  shuffleDeck,
  drawReading,
  parseReadingFromQuery,
} from 'tarot/readingEngine';
import TarotCardFace from 'components/tarot/TarotCardFace';
import TarotReadingDetail from 'components/tarot/TarotReadingDetail';
import TarotLottoBridge from 'components/tarot/TarotLottoBridge';
import TarotShareCard from 'components/tarot/TarotShareCard';

const PHASE = {
  INTRO: 'intro',
  SPREAD: 'spread',
  SHUFFLE: 'shuffle',
  PICK: 'pick',
  REVEAL: 'reveal',
  RESULT: 'result',
};

export default function TarotExperience() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [phase, setPhase] = useState(PHASE.INTRO);
  const [spreadId, setSpreadId] = useState('lotto');
  const [deck, setDeck] = useState([]);
  const [pickedIds, setPickedIds] = useState([]);
  const [revealIndex, setRevealIndex] = useState(0);
  const [reading, setReading] = useState(null);

  const spread = TAROT_SPREADS[spreadId] ?? TAROT_SPREADS.lotto;
  const needed = spread.cardCount;

  const sharedReading = useMemo(() => {
    const s = searchParams.get('spread');
    const c = searchParams.get('cards');
    return parseReadingFromQuery(s, c);
  }, [searchParams]);

  useEffect(() => {
    if (sharedReading) {
      setReading(sharedReading);
      setSpreadId(sharedReading.spreadId);
      setPhase(PHASE.RESULT);
      return;
    }
    const spreadParam = searchParams.get('spread');
    if (spreadParam && TAROT_SPREADS[spreadParam]) {
      setSpreadId(spreadParam);
    }
  }, [sharedReading, searchParams]);

  const startShuffle = (id = spreadId) => {
    setSpreadId(id);
    setPickedIds([]);
    setRevealIndex(0);
    setReading(null);
    setDeck(shuffleDeck(Date.now()));
    setPhase(PHASE.SHUFFLE);
    setTimeout(() => setPhase(PHASE.PICK), 1800);
  };

  const handlePick = (cardId) => {
    if (pickedIds.includes(cardId)) return;
    const next = [...pickedIds, cardId];
    setPickedIds(next);
    if (next.length >= needed) {
      const result = drawReading(spreadId, next);
      setReading(result);
      setPhase(PHASE.REVEAL);
      setRevealIndex(0);
      if (result) {
        setSearchParams({
          spread: result.spreadId,
          cards: result.positions.map((p) => p.card.id).join('-'),
        });
      }
    }
  };

  useEffect(() => {
    if (phase !== PHASE.REVEAL || !reading) return undefined;
    if (revealIndex >= needed - 1) {
      const t = setTimeout(() => setPhase(PHASE.RESULT), 900);
      return () => clearTimeout(t);
    }
    const t = setTimeout(() => setRevealIndex((i) => i + 1), 700);
    return () => clearTimeout(t);
  }, [phase, revealIndex, needed, reading]);

  const reset = () => {
    setSearchParams({});
    setPhase(PHASE.INTRO);
    setPickedIds([]);
    setReading(null);
    setRevealIndex(0);
  };

  if (phase === PHASE.INTRO) {
    return (
      <div className="tarot-step">
        <div className="tarot-hero">
          <p className="tarot-hero__eyebrow">ไพ่ยิปซีออนไลน์ฟรี</p>
          <h1 className="tarot-hero__title">คำทำนายจากไพ่ Major Arcana</h1>
          <p className="tarot-hero__desc">
            เปิดไพ่ 22 ใบศักดิ์สิทธิ์ รับคำทำนายละเอียดด้านความรัก การงาน การเงิน
            และเชื่อมเลขนำโชคกับสถิติหวย Hotto 32 ปี
          </p>
        </div>
        <div className="tarot-actions">
          <button type="button" className="tarot-btn tarot-btn--gold" onClick={() => startShuffle('lotto')}>
            ✨ เปิดไพ่เลขนำโชค (แนะนำ)
          </button>
          <button type="button" className="tarot-btn tarot-btn--primary" onClick={() => setPhase(PHASE.SPREAD)}>
            เลือกรูปแบบการเปิดไพ่
          </button>
        </div>
      </div>
    );
  }

  if (phase === PHASE.SPREAD) {
    return (
      <div className="tarot-step">
        <h2 className="tarot-step__title">เลือกรูปแบบการเปิดไพ่</h2>
        <div className="tarot-spread-grid">
          {Object.values(TAROT_SPREADS).map((s) => (
            <button
              key={s.id}
              type="button"
              className={`tarot-spread-card${s.hotto ? ' tarot-spread-card--hotto' : ''}${spreadId === s.id ? ' tarot-spread-card--active' : ''}`}
              onClick={() => setSpreadId(s.id)}
            >
              <span className="tarot-spread-card__icon" aria-hidden="true">{s.icon}</span>
              <div>
                <p className="tarot-spread-card__name">{s.nameTh}</p>
                <p className="tarot-spread-card__desc">{s.description}</p>
                {s.hotto && <span className="tarot-spread-card__badge">เชื่อมเลขเด็ด Hotto</span>}
              </div>
            </button>
          ))}
        </div>
        <div className="tarot-actions">
          <button type="button" className="tarot-btn tarot-btn--primary" onClick={() => startShuffle(spreadId)}>
            สับไพ่และเริ่มเลือก
          </button>
          <button type="button" className="tarot-btn tarot-btn--ghost" onClick={() => setPhase(PHASE.INTRO)}>
            ย้อนกลับ
          </button>
        </div>
      </div>
    );
  }

  if (phase === PHASE.SHUFFLE) {
    return (
      <div className="tarot-shuffle" role="status" aria-live="polite">
        <div className="tarot-shuffle__deck" aria-hidden="true">
          <div className="tarot-shuffle__card" />
          <div className="tarot-shuffle__card" />
          <div className="tarot-shuffle__card" />
        </div>
        <p>กำลังสับไพ่… ตั้งจิตถามคำถามในใจ</p>
      </div>
    );
  }

  if (phase === PHASE.PICK) {
    return (
      <div className="tarot-step">
        <h2 className="tarot-step__title">{spread.nameTh}</h2>
        <p className="tarot-pick-hint">แตะไพ่ {needed} ใบที่เรียกคุณ — ฟังสัญชาตญาณ</p>
        <p className="tarot-pick-counter">เลือกแล้ว {pickedIds.length}/{needed}</p>
        <div className="tarot-pick-grid">
          {deck.map((card) => (
            <TarotCardFace
              key={card.id}
              faceDown
              selected={pickedIds.includes(card.id)}
              onClick={() => handlePick(card.id)}
              size="md"
            />
          ))}
        </div>
        <div className="tarot-actions">
          <button type="button" className="tarot-btn tarot-btn--ghost" onClick={reset}>
            เริ่มใหม่
          </button>
        </div>
      </div>
    );
  }

  if (phase === PHASE.REVEAL && reading) {
    return (
      <div className="tarot-step">
        <h2 className="tarot-step__title">เปิดเผยไพ่…</h2>
        <div className="tarot-reveal-row">
          {reading.positions.map((pos, i) => (
            <div key={pos.key} className="tarot-position-wrap">
              <p className="tarot-position-label">{pos.labelTh}</p>
              <TarotCardFace
                card={pos.card}
                faceDown={i > revealIndex}
                revealed={i <= revealIndex}
                size="lg"
              />
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (phase === PHASE.RESULT && reading) {
    return (
      <div className="tarot-step">
        <div className="tarot-hero">
          <p className="tarot-hero__eyebrow">{reading.spread.nameTh}</p>
          <h2 className="tarot-hero__title">คำทำนายของคุณ</h2>
          <p className="tarot-hero__desc">{reading.synthesis}</p>
        </div>

        <div className="tarot-result-cards">
          {reading.positions.map((pos) => (
            <div key={pos.key} className="tarot-position-wrap">
              <p className="tarot-position-label">{pos.labelTh}</p>
              <TarotCardFace card={pos.card} size="lg" />
            </div>
          ))}
        </div>

        <TarotShareCard reading={reading} />

        <div className="tarot-synthesis">
          <h3 className="tarot-synthesis__title">สรุปภาพรวม</h3>
          <p className="tarot-synthesis__text">{reading.synthesis}</p>
        </div>

        {reading.positions.map((pos) => (
          <TarotReadingDetail key={pos.key} position={pos} card={pos.card} />
        ))}

        <TarotLottoBridge reading={reading} />

        <div className="tarot-actions">
          <button type="button" className="tarot-btn tarot-btn--gold" onClick={() => startShuffle('lotto')}>
            เปิดไพ่เลขนำโชคอีกครั้ง
          </button>
          <button type="button" className="tarot-btn tarot-btn--primary" onClick={() => { setSearchParams({}); setPhase(PHASE.SPREAD); }}>
            เลือกรูปแบบอื่น
          </button>
          <button type="button" className="tarot-btn tarot-btn--ghost" onClick={reset}>
            กลับหน้าแรกไพ่
          </button>
        </div>
      </div>
    );
  }

  return null;
}
